import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Battery, ChevronRight, Phone, Zap, Shield, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAQMobileChargers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      q: 'What is BIS certification and why is it important for chargers?',
      a: 'BIS (Bureau of Indian Standards) certification is mandatory for mobile chargers sold in India. It ensures products meet electrical safety standards - protection against short circuits, overheating, and electrical shocks. All RSD chargers are BIS certified, ensuring safe use and legal compliance for sellers.'
    },
    {
      q: 'What is the difference between Quick Charge (QC) and Power Delivery (PD)?',
      a: 'Quick Charge (QC) is Qualcomm\'s proprietary fast charging (up to 100W), mainly for Android devices. Power Delivery (PD) is a universal USB standard (up to 240W) that works with iPhones, Android, and laptops. PD is more versatile; QC is widely compatible with budget/mid-range Android phones.'
    },
    {
      q: 'What wattage charger should I choose?',
      a: '15-20W: Budget phones, older devices. 25-33W: Mid-range phones, iPhones. 45W: Flagship phones, tablets. 65W: Multi-device charging, some laptops. Choose based on your device\'s maximum supported charging speed - using higher wattage than supported won\'t damage the device but won\'t charge faster either.'
    },
    {
      q: 'What is VOOC/Super VOOC charging?',
      a: 'VOOC (Voltage Open Loop Multi-step Constant-Current Charging) is OPPO\'s proprietary fast charging technology. Super VOOC can deliver 65W-120W while keeping the phone cool. Requires both a VOOC charger AND VOOC cable - regular cables won\'t enable VOOC speeds.'
    },
    {
      q: 'Can I use a higher wattage charger than my phone supports?',
      a: 'Yes, it\'s completely safe. Modern phones negotiate power with the charger and only draw what they need. A 65W charger connected to a phone supporting 25W will only deliver 25W. However, you won\'t get faster charging beyond your phone\'s limit.'
    },
    {
      q: 'What is GaN (Gallium Nitride) charger technology?',
      a: 'GaN chargers use Gallium Nitride semiconductors instead of silicon. Benefits: 1) Smaller size at same wattage, 2) Less heat generation, 3) Higher efficiency, 4) More durable. RSD\'s 65W GaN chargers are 40% smaller than traditional 65W chargers.'
    },
    {
      q: 'Are RSD chargers safe to use overnight?',
      a: 'Yes, all RSD chargers have multiple safety protections: Over-voltage protection (OVP), Over-current protection (OCP), Short-circuit protection, Over-temperature protection (OTP), and auto-cutoff when battery is full. Combined with modern smartphone battery management, overnight charging is safe.'
    },
    {
      q: 'What is the difference between single port and dual port chargers?',
      a: 'Single port: Maximum power to one device, compact size. Dual port: Charge two devices simultaneously, but power is shared (e.g., 65W total might split to 45W+20W or 30W+30W depending on design). Choose dual port for convenience, single port for maximum speed.'
    },
    {
      q: 'Which charger works with Samsung phones?',
      a: 'Samsung uses Adaptive Fast Charging (AFC) for older models and PD for newer flagships. Our 25W and 45W PD chargers are fully compatible with Samsung Galaxy S/Note series. For older Samsung phones, QC 2.0 chargers work well.'
    },
    {
      q: 'Which charger is best for iPhone fast charging?',
      a: 'iPhones (8 and later) support 20W PD fast charging. Our 20W PD Type-C charger paired with Type-C to Lightning cable provides fastest iPhone charging. iPhone 15 and later use Type-C directly, so Type-C to Type-C cable works.'
    },
    {
      q: 'What does "multi-protocol" charger mean?',
      a: 'Multi-protocol chargers support multiple fast charging standards simultaneously - QC, PD, AFC, VOOC, etc. The charger automatically detects your device and delivers the optimal charging protocol. Great for households with different phone brands.'
    },
    {
      q: 'How do I know if my charger is genuinely fast charging?',
      a: 'Signs of active fast charging: 1) Phone displays "Fast Charging" or "Super Fast Charging", 2) Noticeably faster than standard charging (0-50% in 30 mins for modern phones), 3) Charger may feel slightly warm (normal). If using correct charger+cable but no fast charging, check cable quality.'
    },
    {
      q: 'What is the MOQ for bulk charger orders?',
      a: 'Minimum Order Quantity for BIS-certified chargers: 15W-20W: 10,000 units, 25W-35W: 7,500 units, 45W-65W: 5,000 units. Flexible MOQ for new distributors. Volume discounts available for larger orders. Contact sales for OEM/custom branding orders.'
    },
    {
      q: 'Does RSD provide OEM charger manufacturing?',
      a: 'Yes, RSD offers complete OEM charger manufacturing: custom branding/logo, custom colors (including matte metallic finishes), private label packaging, custom wattage specifications, and BIS certification support. 100+ brands trust RSD for private label chargers.'
    },
    {
      q: 'What safety certifications do RSD chargers have?',
      a: 'All RSD chargers have: BIS certification (mandatory in India), Legal Metrology compliance, CE marking (for exports), and undergo 100% safety testing including overvoltage, overcurrent, short circuit, temperature, and surge protection testing.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mobile Charger FAQ - BIS Certified Charger Questions | RSD Accessories</title>
        <meta name="description" content="Expert answers to mobile charger questions - BIS certification, QC vs PD, wattage guide, VOOC charging, safety features. From India&apos;s leading charger manufacturer." />
        <meta name="keywords" content="mobile charger faq, bis certification charger, quick charge vs pd, vooc charger, charger wattage guide, gan charger, fast charging explained" />
        <link rel="canonical" href="https://rsdaccessories.com/faq/mobile-chargers" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <div className="bg-neutral-100 py-3">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-neutral-500 hover:text-[#FF4D00]">Home</Link>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              <Link to="/faq" className="text-neutral-500 hover:text-[#FF4D00]">FAQ</Link>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              <span className="text-neutral-900 font-medium">Mobile Chargers</span>
            </div>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-br from-neutral-900 to-black py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Battery className="w-16 h-16 text-[#FF4D00] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Mobile Charger FAQs
            </h1>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
              Everything about BIS certification, fast charging protocols, wattage selection, and safety
            </p>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-8 bg-[#FF4D00]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
              <div>
                <Zap className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">15W-65W</div>
                <div className="text-sm text-white/80">Wattage Range</div>
              </div>
              <div>
                <Battery className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">15,000+</div>
                <div className="text-sm text-white/80">Daily Production</div>
              </div>
              <div>
                <Shield className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">BIS</div>
                <div className="text-sm text-white/80">Certified</div>
              </div>
              <div>
                <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">100%</div>
                <div className="text-sm text-white/80">Safety Tested</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8">{faqs.length} Questions About Mobile Chargers</h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow border border-neutral-100 hover:border-[#FF4D00] transition-colors">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3 flex items-start gap-3">
                    <span className="bg-[#FF4D00] text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                      {idx + 1}
                    </span>
                    {faq.q}
                  </h3>
                  <p className="text-neutral-600 ml-11">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Order BIS Certified Chargers?</h2>
            <p className="text-white/90 mb-8">Wholesale prices on 15W to 65W chargers - QC, PD, VOOC support</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/shop/mobile-chargers">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00]">
                  View Charger Range
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQMobileChargers;
